import { z } from 'zod'
import type { PuttMark } from './tileViewModel'

/**
 * The `race_event` types the Birdie board consumes DIRECTLY, and which must
 * therefore never reach `raceEvents`.
 *
 * `getLatestEvent` (`utils/raceUtils.ts:21-25`) has NO type filter and ~18
 * shared call sites read it — countdown, status pill, banner, toolbar, social
 * table. A `putt_result` row inside `raceEvents` would become "the latest
 * event" three times per Birdie round and collapse the shared countdown
 * mid-clip.
 *
 * Membership is by EXACT type name, never by "a type I do not recognise": the
 * counter games' engines already emit `race_paused` / `race_resumed` in
 * production and those must keep flowing into `raceEvents` exactly as today.
 */
export const PUTT_RESULT_EVENT_TYPE = 'putt_result'
/** The live reveal of the round's type: one row, armed by the engine on the last putt's cup tell. */
export const ROUND_TYPE_EVENT_TYPE = 'round_type'
export const BIRDIE_PROGRESS_EVENT_TYPES: ReadonlySet<string> = new Set([
  PUTT_RESULT_EVENT_TYPE,
  ROUND_TYPE_EVENT_TYPE
])

export type PuttIndex = 1 | 2 | 3
export type PuttResultCode = 'H' | 'M'

/**
 * The round's TYPE as the putt rows reveal it, lowercase — the same three the settle reveal names
 * (`settlement.SettledRoundType`), in the live vocabulary.
 *
 * It is revealed on the LAST putt and nothing earlier (owner 2026-09-22: the rendered variant keeps
 * the green plain until the final putt, and the board must not say what the picture has not): a
 * `round_type` row the engine arms on that putt's cup tell, 0.6 s before the stroke, and — as the
 * at-most-once fallback — the last putt row. The type is drawn before betting opens, and
 * `race_started` still lands inside the backend's post-close bet-acceptance grace, so a type on that
 * event would be a side bet whose answer is public while it can still be backed. Both rows go out
 * while the clip is already playing, seconds past that grace.
 */
export type RevealedRoundType = 'plain' | 'frost' | 'fire'

/** The three the engine publishes, and undefined for anything else — never a guess. */
export function revealedRoundType(value: unknown): RevealedRoundType | undefined {
  if (typeof value !== 'string') return undefined
  switch (value.toLowerCase()) {
    case 'plain':
      return 'plain'
    case 'frost':
      return 'frost'
    case 'fire':
      return 'fire'
    default:
      return undefined
  }
}

/** The row shape both the socket path and the hydration path can supply. */
export interface BirdieProgressRowLike {
  event_type: string
  race_id: string
  payload?: unknown
}

export const isBirdieProgressRow = (row: { event_type: string }): boolean =>
  BIRDIE_PROGRESS_EVENT_TYPES.has(row.event_type)

/**
 * Engine contract (E1, spec §1). `clip_frame` / `t_s` are diagnostics the
 * widget never reads; zod strips them. `version` is a hard gate — a v2 payload
 * may mean anything, so it is dropped and logged rather than half-read.
 */
const puttResultPayloadSchema = z.object({
  version: z.literal(1),
  putt: z.union([z.literal(1), z.literal(2), z.literal(3)]),
  result: z.union([z.literal('H'), z.literal('M')]),
  holed_so_far: z.number().int().min(0).max(3),
  // Held as `unknown` and narrowed after: the ring is this payload's job and the type is a
  // decoration on it, so a drifted or unknown value must cost the putt nothing.
  round_type: z.unknown()
})

export interface ParsedPuttResult {
  putt: PuttIndex
  result: PuttResultCode
  /** Inclusive of this putt. Redundant with the earlier events, kept because
   *  the hydration path may see only a subset of the rows. */
  holedSoFar: number
  /** The round's type, on every row of a typed round; absent on an engine that publishes none. */
  roundType?: RevealedRoundType
}

export function parsePuttResult(row: {
  event_type: string
  payload?: unknown
}): ParsedPuttResult | undefined {
  if (row.event_type !== PUTT_RESULT_EVENT_TYPE) return undefined
  const parsed = puttResultPayloadSchema.safeParse(row.payload)
  if (!parsed.success) return undefined
  const roundType = revealedRoundType(parsed.data.round_type)
  return {
    putt: parsed.data.putt,
    result: parsed.data.result,
    holedSoFar: parsed.data.holed_so_far,
    ...(roundType === undefined ? {} : { roundType })
  }
}

/**
 * Engine contract for the reveal row. `clip_frame` / `t_s` are diagnostics; `version` is the same
 * hard gate as the putt row's. A type the board cannot place is dropped — undefined leaves both
 * round-type tiles alive, where a guess would dim the winner.
 */
const roundTypePayloadSchema = z.object({
  version: z.literal(1),
  round_type: z.unknown()
})

export function parseRoundTypeReveal(row: {
  event_type: string
  payload?: unknown
}): RevealedRoundType | undefined {
  if (row.event_type !== ROUND_TYPE_EVENT_TYPE) return undefined
  const parsed = roundTypePayloadSchema.safeParse(row.payload)
  if (!parsed.success) return undefined
  return revealedRoundType(parsed.data.round_type)
}

/**
 * The type the board may DRAW right now: the revealed one once its hold has elapsed. The same hold
 * as a ring (`BIRDIE_RESULT_HOLD_MS`, the stream's playout maximum), measured from when this
 * client received the row, so the tiles never resolve before the viewer's own picture has lit the
 * cup. Undefined until then.
 */
export function roundTypeVisible(input: {
  roundType: RevealedRoundType | undefined
  revealedAtMs: number | undefined
  nowMs: number
  holdMs: number
}): RevealedRoundType | undefined {
  if (input.roundType === undefined) return undefined
  if (input.revealedAtMs !== undefined && input.revealedAtMs + input.holdMs > input.nowMs)
    return undefined
  return input.roundType
}

export interface PuttEntry {
  result: PuttResultCode
  /** When the widget received the row — the hold is measured from here, NOT
   *  from `event_timestamp`, so a late socket delivery cannot lead the video. */
  receivedAtMs: number
}

/** Keyed by putt index, so duplicate and out-of-order delivery are harmless. */
export type PuttEntries = Partial<Record<PuttIndex, PuttEntry>>

export const PUTT_ORDER: readonly PuttIndex[] = [1, 2, 3]

export const markForResult = (result: PuttResultCode): PuttMark =>
  result === 'H' ? 'holed' : 'missed'

export interface PuttsVisibleInput {
  putts: PuttEntries | undefined
  nowMs: number
  holdMs: number
}

/**
 * The rings the card may draw right now.
 *
 * Two rules, both spoiler rules:
 *  - a putt is invisible until its own hold has elapsed (spec §3, the second
 *    of the two delays that keep the card behind the picture);
 *  - the walk STOPS at the first putt that is missing or still held. Putt 3 is
 *    never allowed to slide into slot 1 — that would show the viewer a result
 *    for a putt they have not watched yet.
 */
export function puttsVisible(input: PuttsVisibleInput): PuttMark[] {
  const out: PuttMark[] = []
  const putts = input.putts
  if (putts === undefined) return out
  for (const index of PUTT_ORDER) {
    const entry = putts[index]
    if (entry === undefined) break
    if (entry.receivedAtMs + input.holdMs > input.nowMs) break
    out.push(markForResult(entry.result))
  }
  return out
}

/**
 * Ms until the next held putt becomes visible, or `null` when nothing is pending: every received
 * putt is already visible, or the walk stops at a putt that has not arrived. The host arms a
 * one-shot timer on it — the live putts used to ride the board's 1 Hz tick, which landed a ring
 * anywhere inside the second after its hold (owner 2026-09-23: the board trailed the ball by
 * 2-3 s). Same treatment the round type and the settlement already get.
 */
export function nextPuttRevealDelayMs(input: PuttsVisibleInput): number | null {
  const putts = input.putts
  if (putts === undefined) return null
  for (const index of PUTT_ORDER) {
    const entry = putts[index]
    if (entry === undefined) return null
    const due = entry.receivedAtMs + input.holdMs - input.nowMs
    if (due > 0) return due
  }
  return null
}

/** What the card / mobile strip need to draw a ring row. */
export interface BirdieRingsView {
  marks: readonly PuttMark[]
  delaysMs: readonly number[]
  label?: string
  /** B34: two putts in and the third under way — the pending slot burns. */
  hot?: boolean
}

/**
 * Scale-in delays for a LIVE ring row.
 *
 * A ring that was already on screen gets 0 — its CSS animation has long since
 * finished and its slot keeps its DOM node, so it never re-animates. Rings that
 * arrived since the last render are staggered, which only ever matters for a
 * catch-up burst (hydration after a mid-round reload); the ordinary one-at-a-
 * time arrival lands the single new ring immediately.
 */
export function ringDelaysForLive(
  count: number,
  alreadyShown: number,
  staggerMs: number
): number[] {
  const out: number[] = []
  for (let i = 0; i < count; i++) out.push(i < alreadyShown ? 0 : (i - alreadyShown) * staggerMs)
  return out
}
